/**
 * The interactive tools: gate, exposure, risk report, file-a-report, vendors, unsubscribe.
 *
 * These prerender to static HTML like everything else and then hydrate — a crawler gets the
 * explanation and the form labels, a person gets a working form. Every one posts to a real
 * endpoint; there are no dead buttons on this surface.
 */
import { useState, type ReactNode } from "react"
import {
  registry,
  type DisputeFiled,
  type ExposureEstimate,
  type GateDecision,
  type RiskReport,
} from "@agitome/ui-shared"
import { Shell , trackToolCompleted, trackToolStarted } from "@agitome/ui-shared"
import { Capture, Rules, TierPill } from "@agitome/ui-shared"

function Hero({ band, eyebrow, title, lede }: { band: string; eyebrow: string; title: ReactNode; lede: string }) {
  return (
    <div className={`band ${band} band--grain`}>
      <div className="wrap">
        <p className="eyebrow eyebrow--accent">{eyebrow}</p>
        <h1 className="mega mt-4 balance" style={{ maxWidth: "17ch" }}>{title}</h1>
        <p className="lede mt-6 measure">{lede}</p>
      </div>
    </div>
  )
}

function Problem({ error }: { error: string | null }) {
  if (!error) return null
  return (
    <div className="well mt-5" role="alert">
      <strong>That did not run.</strong>
      <p className="fine mt-2">{error}</p>
    </div>
  )
}

// ── /gate ──────────────────────────────────────────────────────────────────

export function Gate() {
  const [framework, setFramework] = useState("")
  const [model, setModel] = useState("")
  const [tier, setTier] = useState("moderate")
  const [onUnknown, setOnUnknown] = useState("review")
  const [reasonClass, setReasonClass] = useState("")
  const [result, setResult] = useState<GateDecision | null>(null)
  const [error, setError] = useState<string | null>(null)

  return (
    <Shell current="/gate">
      <Hero
        band="band--ink band--mesh"
        eyebrow="The admission gate"
        title={<>Should you <span className="hot">ship</span> this?</>}
        lede="Your policy, over reported data, before the thing reaches production. agitome does not decide for you — it applies the thresholds you set and prints why."
      />
      <div className="band band--paper">
        <div className="wrap">
          <div className="grid grid-sidebar">
            <div>
              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  setError(null)
                  // The pair is the activation rate. Started alone counts intent; completed
                  // alone hides how many people asked and got nothing.
                  trackToolStarted("gate")
                  const startedAt = Date.now()
                  registry
                    .gate({
                      framework: framework || undefined,
                      model: model || undefined,
                      max_acceptable_tier: tier,
                      on_unknown: onUnknown,
                      deny_on_reason_class: reasonClass || undefined,
                    })
                    .then((r) => {
                      setResult(r)
                      // The DECISION, never the subject. What somebody chose to run through
                      // their own admission policy is their business, and putting it in an
                      // analytics vendor is a different promise from the one /privacy makes.
                      trackToolCompleted("gate", r.decision, { duration_ms: Date.now() - startedAt })
                    })
                    .catch((err: Error) => {
                      setError(err.message)
                      trackToolCompleted("gate", "failed", { duration_ms: Date.now() - startedAt })
                    })
                }}
              >
                <h2 className="h2">What are you admitting?</h2>
                <div className="grid grid-2 mt-5">
                  <label className="label"><span>Framework</span>
                    <input className="field" value={framework} placeholder="LangChain"
                      onChange={(e) => setFramework(e.target.value)} /></label>
                  <label className="label"><span>Model</span>
                    <input className="field" value={model} placeholder="gpt-4o"
                      onChange={(e) => setModel(e.target.value)} /></label>
                </div>

                {/* aria-labelledby, NOT aria-label. Each control's visible label is already
                    on screen in the <dt> beside it — but a <dt> associates with nothing, so a
                    screen reader announced "combobox, Low" on a form whose entire purpose is
                    that these are YOUR thresholds. Pointing at the <dt> rather than retyping
                    the string means the spoken name and the printed one cannot drift. */}
                <h2 className="h2 mt-8">Your policy</h2>
                <p className="fine mt-2">These are your thresholds, not ours. Change them and the verdict changes.</p>
                <dl className="spec mt-5">
                  <div><dt id="gate-tier">Accept up to tier</dt><dd>
                    <select className="field" style={{ maxWidth: "14rem" }} value={tier}
                      aria-labelledby="gate-tier"
                      onChange={(e) => setTier(e.target.value)}>
                      <option value="low">Low</option><option value="moderate">Moderate</option>
                      <option value="elevated">Elevated</option><option value="high">High</option>
                    </select></dd></div>
                  <div><dt id="gate-reason">Deny on failure mode</dt><dd>
                    <select className="field" style={{ maxWidth: "14rem" }} value={reasonClass}
                      aria-labelledby="gate-reason"
                      onChange={(e) => setReasonClass(e.target.value)}>
                      <option value="">None</option><option value="security">Security — any</option>
                      <option value="correctness">Correctness — any</option>
                      <option value="policy">Policy — any</option>
                      <option value="availability">Availability — any</option>
                    </select></dd></div>
                  <div><dt id="gate-unknown">Treat insufficient as</dt><dd>
                    <select className="field" style={{ maxWidth: "14rem" }} value={onUnknown}
                      aria-labelledby="gate-unknown"
                      onChange={(e) => setOnUnknown(e.target.value)}>
                      <option value="review">Review</option><option value="allow">Allow</option>
                      <option value="deny">Deny</option>
                    </select></dd></div>
                </dl>
                <p className="mt-6">
                  <button className="btn btn--primary btn--lg" type="submit"
                    disabled={!framework && !model}>Run the gate</button>
                </p>
              </form>

              <Problem error={error} />

              {result ? (
                <div className="mt-8">
                  <p className="eyebrow">Verdict</p>
                  <div className="slab mt-3">
                    <div className="row row--between">
                      <p className="display" style={{ fontSize: "var(--t-h1)" }}>
                        {result.decision.toUpperCase()}
                      </p>
                      <TierPill tier={result.tier} />
                    </div>
                    <p className="mt-5">{result.reason}</p>
                  </div>
                  <p className="eyebrow mt-6">Why — every rule that fired</p>
                  <Rules rules={result.rules} />
                  {result.subjects.length > 1 ? (
                    <>
                      <p className="eyebrow mt-6">Each subject</p>
                      <dl className="spec mt-3">
                        {result.subjects.map((s) => (
                          <div key={`${s.subject_kind}:${s.subject_slug}`}>
                            <dt>{s.subject_slug}</dt>
                            <dd>{s.decision} — {s.reason}</dd>
                          </div>
                        ))}
                      </dl>
                      <p className="fine mt-3">
                        A stack passes only if every part of it does. The verdict above is the worst
                        of these, never an average — an elevated framework under a low-risk model is
                        an elevated framework.
                      </p>
                    </>
                  ) : null}
                  <p className="fine mt-4">
                    <strong>This verdict is reproducible.</strong> The same policy over the same
                    corpus returns the same answer, and the rules above are the whole of it.
                  </p>
                  <p className="fine mt-3">{result.note}</p>
                </div>
              ) : null}
            </div>

            <aside className="stack-lg">
              <div className="card">
                <h3 className="h3">Wire it into CI</h3>
                <p className="fine mt-3">The same gate over HTTP, so a pull request that adds a dependency fails before it merges.</p>
                <pre className="card card--flat mono fine mt-4" style={{ overflowX: "auto", padding: "var(--s4)" }}>{`POST /risk-gate
{
  "framework": "langchain",
  "max_acceptable_tier": "moderate",
  "deny_on_reason_class": "security"
}`}</pre>
                <p className="fine mt-3">No account needed to try it.</p>
              </div>
              <div className="card">
                <h3 className="h3">Watch this decision</h3>
                <Capture source="gate_check" cta="Watch" />
              </div>
              <div className="card card--flat">
                <p className="fine">
                  <strong>The gate is yours, not ours.</strong> agitome publishes tiers. Deciding
                  what tier is acceptable for your risk appetite is a judgement we are not
                  positioned to make, so the thresholds live with you.
                </p>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </Shell>
  )
}

// ── /exposure ──────────────────────────────────────────────────────────────

export function Exposure() {
  const [actions, setActions] = useState("120000")
  const [value, setValue] = useState("340.00")
  const [reversible, setReversible] = useState("0")
  const [reviewed, setReviewed] = useState("0")
  const [result, setResult] = useState<ExposureEstimate | null>(null)
  const [error, setError] = useState<string | null>(null)

  return (
    <Shell current="/exposure">
      <Hero
        band="band--ink band--mesh"
        eyebrow="The exposure calculator"
        title={<>What could this cost you if it goes <span className="hot">wrong</span>?</>}
        lede="Your numbers, plus an honest answer about insurability. This is arithmetic, not a quote — and it will not become one until the loss data exists to price against."
      />
      <div className="band band--paper">
        <div className="wrap">
          <div className="grid grid-sidebar">
            <div>
              <form onSubmit={(e) => {
                e.preventDefault()
                setError(null)
                trackToolStarted("exposure")
                const startedAt = Date.now()
                registry
                  .exposure({
                    annual_actions: Number(actions),
                    value_at_risk_per_action: value,
                    reversible_share: reversible,
                    reviewed_share: reviewed,
                  })
                  .then((r) => {
                    setResult(r)
                    // Not the number. Somebody's annual action volume and value at risk are the
                    // most commercially sensitive things they will type into this product.
                    trackToolCompleted("exposure", "estimated", { duration_ms: Date.now() - startedAt })
                  })
                  .catch((err: Error) => {
                    setError(err.message)
                    trackToolCompleted("exposure", "failed", { duration_ms: Date.now() - startedAt })
                  })
              }}>
                <h2 className="h2">Your deployment</h2>
                <div className="grid grid-2 mt-5">
                  <label className="label"><span>Actions per year</span>
                    <input className="field" value={actions} onChange={(e) => setActions(e.target.value)} /></label>
                  <label className="label"><span>Value at risk per action</span>
                    <input className="field" value={value} onChange={(e) => setValue(e.target.value)} /></label>
                  <label className="label"><span>Share reversible in time</span>
                    <select className="field" value={reversible} onChange={(e) => setReversible(e.target.value)}>
                      <option value="0">None</option><option value="0.25">A quarter</option>
                      <option value="0.5">Half</option><option value="0.68">About two thirds</option>
                      <option value="0.9">Nearly all</option>
                    </select></label>
                  <label className="label"><span>Share a human reviews first</span>
                    <select className="field" value={reviewed} onChange={(e) => setReviewed(e.target.value)}>
                      <option value="0">None</option><option value="0.02">Sampled 2%</option>
                      <option value="0.5">Half</option><option value="1">All of them</option>
                    </select></label>
                </div>
                <p className="fine mt-3 measure">
                  Both mitigation figures are <strong>yours</strong>. agitome supplies no
                  coefficient — a "reversibility relief" of ours would be a loss distribution we
                  have not earned, wearing the costume of a fact about your deployment.
                </p>
                <p className="mt-6"><button className="btn btn--primary btn--lg" type="submit">Estimate exposure</button></p>
              </form>

              <Problem error={error} />

              {result ? (
                <div className="mt-8">
                  <div className="slab">
                    <p className="eyebrow">Unmitigated annual exposure</p>
                    <p className="mega mt-2" style={{ fontSize: "clamp(2.5rem,8vw,5rem)" }}>
                      {result.unmitigated_annual_exposure} {result.currency}
                    </p>
                    <p className="mt-4 fine">
                      Notional, before your mitigations: {result.notional_annual_exposure} {result.currency}.
                    </p>
                  </div>
                  <dl className="spec mt-8">
                    {result.derivation.map((line, i) => (
                      <div key={i}><dt>{i + 1}</dt><dd>{line}</dd></div>
                    ))}
                  </dl>
                  <div className="card card--flat mt-6" style={{ borderStyle: "dashed" }}>
                    <p><strong>Could anybody underwrite this today?</strong> {result.insurability === "assessable" ? "Yes." : "No."}</p>
                    <p className="fine mt-3">{result.insurability_detail}</p>
                    <p className="eyebrow mt-5">The four dimensions that gate a real quote</p>
                    <dl className="spec mt-3">
                      {result.gate_requirements.map((r) => (
                        <div key={r.dimension}>
                          <dt>{r.dimension.replace(/_/g, " ")} · {r.need.toLocaleString("en-US")}</dt>
                          <dd>{r.detail}</dd>
                        </div>
                      ))}
                    </dl>
                    <p className="fine mt-4">{result.no_price_note}</p>
                  </div>
                </div>
              ) : null}
            </div>
            <aside className="stack-lg">
              <div className="card">
                <h3 className="h3">Tell me when it is insurable</h3>
                <Capture source="exposure_estimate" />
              </div>
              <div className="card card--flat">
                <p className="fine">
                  <strong>Why no price.</strong> The actuarial void is real and naming it is the
                  product. An insurer needs base rates over a population; agent deployments have
                  not generated one. Until they do, this page does arithmetic and stops.
                </p>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </Shell>
  )
}

// ── /risk-report ───────────────────────────────────────────────────────────

export function RiskReportTool() {
  const [fields, setFields] = useState({ framework: "", model: "", vendor: "", agent_did: "" })
  const [result, setResult] = useState<RiskReport | null>(null)
  const [error, setError] = useState<string | null>(null)
  const set = (k: string) => (e: { target: { value: string } }) =>
    setFields((f) => ({ ...f, [k]: e.target.value }))

  return (
    <Shell current="/risk-report">
      <Hero
        band="band--violet"
        eyebrow="The shareable unit"
        title={<>Carfax, for the stack you are about to <span className="hot">ship</span>.</>}
        lede="One report across framework, model and vendor together. It is a snapshot — the link shows whoever opens it exactly what you saw, on the day you saw it."
      />
      <div className="band band--paper">
        <div className="wrap">
          <form onSubmit={(e) => {
            e.preventDefault()
            setError(null)
            const body = Object.fromEntries(Object.entries(fields).filter(([, v]) => v))
            trackToolStarted("risk_report")
            const startedAt = Date.now()
            registry
              .pullReport(body)
              .then((r) => {
                setResult(r)
                // How many subjects they asked about, not which. The stack somebody is
                // evaluating is the single most sensitive thing in this form.
                trackToolCompleted("risk_report", "pulled", {
                  duration_ms: Date.now() - startedAt,
                  subject_count: Object.keys(body).length,
                })
              })
              .catch((err: Error) => {
                setError(err.message)
                trackToolCompleted("risk_report", "failed", { duration_ms: Date.now() - startedAt })
              })
          }}>
            <h2 className="h2">What are you shipping on?</h2>
            <div className="grid grid-2 mt-5" style={{ maxWidth: "46rem" }}>
              <label className="label"><span>Framework</span>
                <input className="field" placeholder="LangChain" onChange={set("framework")} /></label>
              <label className="label"><span>Model</span>
                <input className="field" placeholder="gpt-4o" onChange={set("model")} /></label>
              <label className="label"><span>Vendor agent (optional)</span>
                <input className="field" placeholder="Acme Agents" onChange={set("vendor")} /></label>
              <label className="label"><span>Agent DID (optional)</span>
                <input className="field mono" placeholder="did:key:z6Mk…" onChange={set("agent_did")} /></label>
            </div>
            <p className="mt-6"><button className="btn btn--primary btn--lg" type="submit">Pull the report</button></p>
          </form>

          <Problem error={error} />

          {result ? (
            <div className="mt-8">
              <div className="row row--between">
                <h2 className="h2">{result.headline}</h2>
                <TierPill tier={result.tier} />
              </div>
              <p className="fine mt-2">
                Snapshot <span className="mono">{result.report_id}</span> · {result.generated_at.slice(0, 10)} · immutable
              </p>
              <dl className="spec mt-8">
                {result.subjects.map((s) => (
                  <div key={`${s.subject_kind}:${s.subject_slug}`}>
                    <dt>{s.display_name}</dt>
                    <dd>
                      <TierPill tier={s.tier} /> — {s.counted.total} counted, {s.counted.verified} verified
                      {s.open_disputes > 0 ? `, ${s.open_disputes} open dispute${s.open_disputes === 1 ? "" : "s"}` : ""}
                    </dd>
                  </div>
                ))}
              </dl>
              <div className="card card--flat mt-6" style={{ borderStyle: "dashed" }}>
                <p className="fine">
                  <strong>A stack tier is not the sum of its parts.</strong> The report shows each
                  component's tier separately and does not average them — an elevated framework
                  under a low-risk model is not "moderate", it is an elevated framework.
                </p>
                <p className="fine mt-3">{result.snapshot_note}</p>
              </div>
              <p className="mt-6">
                <button className="btn btn--solid"
                  onClick={() => void navigator.clipboard?.writeText(result.share_url)}>
                  Copy share link
                </button>{" "}
                <a className="btn btn--ghost" href={new URL(result.share_url).pathname}>Open it</a>
              </p>
            </div>
          ) : null}
        </div>
      </div>
    </Shell>
  )
}

// ── /report ────────────────────────────────────────────────────────────────

export function FileReport() {
  const [fields, setFields] = useState<Record<string, string>>({})
  const [filed, setFiled] = useState<{ share_url: string } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const set = (k: string) => (e: { target: { value: string } }) =>
    setFields((f) => ({ ...f, [k]: e.target.value }))

  return (
    <Shell current="/report">
      <Hero
        band="band--ink band--mesh"
        eyebrow="File a report"
        title={<>Something went <span className="hot">wrong</span>. Tell us in a minute.</>}
        lede="Three fields and no account: what happened, the detail, and what it happened to. It publishes immediately as an unverified signal, and it moves nobody's score until somebody adjudicates it."
      />
      <div className="band band--paper">
        <div className="wrap">
          <div className="grid grid-sidebar">
            <div>
              {filed ? (
                <div className="slab">
                  <p className="eyebrow">Filed</p>
                  <p className="mt-3">
                    It is published, marked <span className="badge badge--reported">Reported</span>,
                    and it has its own permanent URL.
                  </p>
                  <p className="mt-5">
                    <a className="btn btn--primary" href={new URL(filed.share_url).pathname}>
                      Open your report
                    </a>
                  </p>
                </div>
              ) : (
                <form className="stack-lg" onSubmit={(e) => {
                  e.preventDefault()
                  setError(null)
                  const body = Object.fromEntries(Object.entries(fields).filter(([, v]) => v))
                  if (body.severity) body.severity = String(Number(body.severity))
                  registry.fileIncident(body).then(setFiled).catch((err: Error) => setError(err.message))
                }}>
                  <label className="label"><span>What happened? · required</span>
                    <input className="field" required
                      placeholder="Agent paid a duplicate invoice after a gateway timeout"
                      onChange={set("title")} /></label>
                  <label className="label"><span>Tell us more · required</span>
                    <textarea className="field" required
                      placeholder="What the agent was doing, what it did instead, and what it cost. Plain language is fine."
                      onChange={set("narrative")} /></label>
                  <p className="fine measure">
                    <strong>Name at least one of the four below.</strong> A report naming nothing
                    cannot appear on any risk profile, so it would be filed where nobody deciding
                    whether to deploy something would ever see it — which is the only reason to
                    file one.
                  </p>
                  <div className="grid grid-2">
                    <label className="label"><span>Framework</span>
                      <input className="field" placeholder="LangChain" onChange={set("framework")} /></label>
                    <label className="label"><span>Model</span>
                      <input className="field" placeholder="gpt-4o" onChange={set("model")} /></label>
                    <label className="label"><span>Vendor</span>
                      <input className="field" placeholder="Acme Agents" onChange={set("vendor")} /></label>
                    <label className="label"><span>Agent DID</span>
                      <input className="field mono" placeholder="did:key:z6Mk…" onChange={set("agent_did")} /></label>
                    <label className="label"><span>Failure mode · optional</span>
                      <select className="field" onChange={set("reason_class")}>
                        <option value="">I'm not sure</option>
                        <option value="correctness">Correctness</option>
                        <option value="security">Security</option>
                        <option value="policy">Policy</option>
                        <option value="availability">Availability</option>
                      </select></label>
                    <label className="label"><span>Severity · optional</span>
                      <select className="field" onChange={set("severity")}>
                        <option value="">Not sure</option>
                        <option value="0">0 — near miss</option><option value="1">1</option>
                        <option value="2">2</option><option value="3">3 — material</option>
                        <option value="4">4</option><option value="5">5 — severe</option>
                      </select></label>
                  </div>
                  <label className="label"><span>Your email · optional</span>
                    <input className="field" type="email" placeholder="you@company.com" onChange={set("email")} />
                    <span className="fine">
                      Recorded so we can tell you if this is adjudicated or disputed. Never
                      published. <a href="/tools/unsubscribe">One click to leave.</a>
                    </span></label>
                  <div>
                    <button
                      className="btn btn--primary btn--lg"
                      type="submit"
                      disabled={
                        !(fields.framework || fields.model || fields.vendor || fields.agent_did)
                      }
                    >
                      File the report
                    </button>
                    {!(fields.framework || fields.model || fields.vendor || fields.agent_did) ? (
                      <p className="fine mt-2">
                        Name a framework, model, vendor or agent DID to enable this.
                      </p>
                    ) : null}
                  </div>
                </form>
              )}
              <Problem error={error} />
            </div>
            <aside className="stack-lg">
              <div className="card">
                <h3 className="h3">"I'm not sure" is a real answer</h3>
                <p className="fine mt-3">
                  Failure mode and severity can stay empty. A reporter forced to guess a failure
                  mode corrupts every count downstream, so guessing is never required — but the
                  subject is not a guess, it is the thing the report is about.
                </p>
              </div>
              <div className="card">
                <h3 className="h3">What happens next</h3>
                <ol className="fine stack mt-3" style={{ paddingLeft: "1.1rem" }}>
                  <li>It publishes immediately, marked <span className="badge badge--reported">Reported</span>.</li>
                  <li>The named party can dispute it, and the clock starts.</li>
                  <li>If a signed loss event backs it, it is adjudicated and becomes evidence.</li>
                </ol>
              </div>
              <div className="card card--flat">
                <p className="fine">
                  <strong>You are naming somebody's product.</strong> Reports are public and
                  attributed to the subject you name. The named party gets a permanent right of
                  reply that we never remove.
                </p>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </Shell>
  )
}

// ── /vendors ───────────────────────────────────────────────────────────────

export function VendorTools() {
  const [ref, setRef] = useState("")
  const [statement, setStatement] = useState("")
  const [grounds, setGrounds] = useState("disputed_facts")
  const [relationship, setRelationship] = useState("")
  const [done, setDone] = useState<DisputeFiled | null>(null)
  const [error, setError] = useState<string | null>(null)

  return (
    <Shell current="/vendors">
      <Hero
        band="band--violet"
        eyebrow="For vendors"
        title={<>This is about my product. What can I <span className="hot">do</span>?</>}
        lede="Four things, and three of them need no credential. The clock on all of them runs in your favour, not ours."
      />
      <div className="band band--bone">
        <div className="wrap">
          <div className="grid grid-sidebar">
            <div>
              <h2 className="h2">Open a dispute</h2>
              <form className="stack-lg mt-5" onSubmit={(e) => {
                e.preventDefault()
                setError(null); setDone(null)
                registry
                  .dispute(ref.trim(), { grounds, statement, relationship: relationship || undefined })
                  .then(setDone)
                  .catch((err: Error) => setError(err.message))
              }}>
                <label className="label"><span>Incident id or slug · required</span>
                  <input className="field mono" required value={ref} placeholder="inc_… or the slug from its URL"
                    onChange={(e) => setRef(e.target.value)} />
                  <span className="fine">
                    It is in the URL of the incident page. <a href="/tools/incidents">Search the registry</a> if you do not have it.
                  </span></label>
                <label className="label"><span>Grounds</span>
                  <select className="field" value={grounds} onChange={(e) => setGrounds(e.target.value)}>
                    <option value="disputed_facts">The facts are wrong</option>
                    <option value="not_our_product">Not our product</option>
                    <option value="already_fixed">Already fixed</option>
                    <option value="duplicate">Duplicate of another report</option>
                  </select></label>
                <label className="label"><span>What is wrong with this report? · required</span>
                  <textarea className="field" required minLength={10} value={statement}
                    placeholder="Which specific facts are contested, and why."
                    onChange={(e) => setStatement(e.target.value)} /></label>
                <label className="label"><span>Your relationship to the subject</span>
                  <input className="field" value={relationship} placeholder="I maintain it"
                    onChange={(e) => setRelationship(e.target.value)} />
                  <span className="fine">
                    Published beside your dispute as your own description. It grants nothing —
                    effect comes from a verified claim and only from that, because a
                    self-description that moved an incident out of the statistics would be a claim
                    system anyone could talk their way through.
                  </span></label>
                <div><button className="btn btn--primary btn--lg" type="submit">Open the dispute</button></div>
              </form>
              <Problem error={error} />
              {done ? (
                <div className="slab mt-5">
                  <p className="eyebrow">Filed</p>
                  <p className="mt-3">
                    Dispute <span className="mono">{done.dispute.dispute_id}</span> is recorded
                    publicly and the clock has started. Thirty days, or forty-five with a recorded
                    extension.
                  </p>
                  {/* What it did and did NOT do. Anyone may dispute; only a verified owner's
                      dispute suspends the incident from published statistics — and a filer who
                      believed otherwise would think they had removed something they annotated. */}
                  <p className="fine mt-3">{done.effect_note}</p>
                  <p className="fine mt-3">
                    {done.incident_resolution_changed
                      ? "This incident has left every published statistic while the dispute runs."
                      : "The incident still counts. Claim the subject and verify it, and a dispute " +
                        "from you takes it out of the statistics while it is contested."}
                  </p>
                </div>
              ) : null}
            </div>
            <aside className="stack-lg">
              <div className="slab">
                <p className="eyebrow">The deadline</p>
                <p className="h1 mt-3">30 days</p>
                <p className="mt-4">
                  45 if we record an extension. Past that the dispute resolves{" "}
                  <strong>in your favour automatically</strong> and the incident stops counting.
                </p>
                <p className="fine mt-4" style={{ color: "inherit", opacity: 0.75 }}>
                  The party that failed to decide in time is agitome, and the cost of that lands
                  here rather than on you.
                </p>
              </div>
              <div className="card">
                <h3 className="h3">Claiming your subject</h3>
                <p className="fine mt-3">
                  A dispute from a verified owner pulls the incident out of published statistics
                  while it runs. Claiming is how you become one — file a claim, present an authoxi
                  tenant credential, and an operator verifies it.
                </p>
                <p className="fine mt-3">
                  Claiming grants nothing until it is verified. Otherwise anyone could mute
                  anything.
                </p>
              </div>
              <div className="card">
                <h3 className="h3">What needs a verified claim, and what does not</h3>
                <dl className="spec mt-3">
                  <div>
                    <dt>Dispute</dt>
                    <dd>
                      Anyone, no account. Only a verified owner's dispute suspends the incident
                      from published statistics while it runs.
                    </dd>
                  </div>
                  <div>
                    <dt>Statement</dt>
                    <dd>
                      Verified owner only. A permanent reply attributed to you has to actually be
                      yours, or the mechanism becomes a way of putting words in your mouth.
                    </dd>
                  </div>
                  <div>
                    <dt>Resolution</dt>
                    <dd>
                      Verified owner only. "We shipped a fix" is a claim about what you did, and it
                      is recorded under your name.
                    </dd>
                  </div>
                </dl>
              </div>
              <div className="card">
                <h3 className="h3">Told when something is filed</h3>
                <Capture source="feature_watch" />
              </div>
            </aside>
          </div>
        </div>
      </div>
    </Shell>
  )
}

// ── /unsubscribe ───────────────────────────────────────────────────────────

export function Unsubscribe() {
  const [email, setEmail] = useState("")
  const [done, setDone] = useState(false)

  return (
    <Shell>
      <div className="band band--bone">
        <div className="wrap wrap--narrow">
          <p className="eyebrow">Leaving</p>
          <h1 className="display mt-4 balance">One click. No login. No "are you sure".</h1>
          <p className="lede mt-6">
            Enter the address and it is gone from everything we hold. We do not send a confirmation
            email to confirm you wanted to stop getting email.
          </p>
          {done ? (
            <div className="slab mt-8">
              <p><strong>Done.</strong> That address is removed.</p>
              <p className="fine mt-3">
                The same answer comes back whether or not it was ever captured — so this page
                cannot be used to test whether somebody is on a list.
              </p>
            </div>
          ) : (
            <form className="capture mt-8" onSubmit={(e) => {
              e.preventDefault()
              // Deliberately ignores failure in the UI: the endpoint is idempotent and answers the
              // same for an address it never held, so there is no failure a person could act on
              // and a red error here would only invite them to try again with variations.
              registry.forget(email).finally(() => setDone(true))
            }}>
              <label className="label"><span>Email address</span>
                <input className="field" type="email" required placeholder="you@company.com"
                  autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} /></label>
              <div className="mt-4"><button className="btn btn--primary btn--lg" type="submit">Remove me</button></div>
            </form>
          )}
          <dl className="spec mt-8">
            <div><dt>What is removed</dt><dd>Every capture tied to that address — subject watches, report follow-ups, the drop, gate alerts. All of it, not a preference centre.</dd></div>
            <div><dt>Idempotent</dt><dd>Doing it twice is the same as doing it once. An address that was never captured returns the same answer, so this page cannot be used to test whether someone is subscribed.</dd></div>
            <div><dt>What we keep</dt><dd>Nothing tied to you. An incident you filed stays published — it is a report about a product, not about you — and it was never attributed to your address publicly.</dd></div>
            <div><dt>How long</dt><dd>Immediate. There is no 10-day processing window.</dd></div>
          </dl>
          <p className="serif mt-8" style={{ fontSize: "var(--t-h3)" }}>
            The exit is on every page that has an entrance. A registry asking people to trust it
            with reports about their employer does not get to make leaving difficult.
          </p>
        </div>
      </div>
    </Shell>
  )
}
